import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import { db } from '../firebase/firebaseConfig';
import { doc, setDoc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import './ChatLegal.css';
import AnalisisPagos from './AnalisisPagos';
import DocumentosRelacionados from './DocumentosRelacionados';

const ChatLegal = () => {
  const [messages, setMessages] = useState([
    { 
      sender: 'system', 
      message: 'Bienvenido al asistente legal especializado en derecho pensional. Puedo ayudarte con:\n\n' +
               '- Consultas sobre pensionados específicos (proporcionando nombre o cédula)\n' +
               '- Información sobre pagos de pensión y sus detalles\n' +
               '- Preguntas sobre la Ley 4 de 1976 y derechos de los pensionados\n\n' +
               '¿En qué puedo ayudarte hoy?'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contextoActivo, setContextoActivo] = useState(null);
  const [ley4Documentos, setLey4Documentos] = useState(null);
  const [mostrarAnalisisPagos, setMostrarAnalisisPagos] = useState(false);
  const [mostrarDocumentosRelacionados, setMostrarDocumentosRelacionados] = useState(false);
  const [datosDocumentosRelacionados, setDatosDocumentosRelacionados] = useState(null);
  const [datosPagos, setDatosPagos] = useState(null);
  const chatWindowRef = useRef(null);
  const { userRole } = useSelector((state) => state.auth);

  // Cargar documentos de la Ley 4 cuando inicie el componente
  useEffect(() => {
    const cargarDocumentosLey4 = async () => {
      if (!ley4Documentos) {
        try {
          console.log("Cargando documentos de la Ley 4 desde Firestore...");
          const documentosLey4 = await obtenerDocumentosLey4();
          setLey4Documentos(documentosLey4);
          console.log("Documentos de la Ley 4 cargados exitosamente");
        } catch (error) {
          console.error("Error al cargar documentos de la Ley 4:", error);
          setError("No se pudieron cargar los documentos de la Ley 4. Algunas consultas podrían tener respuestas limitadas.");
        }
      }
    };
    
    cargarDocumentosLey4();
  }, [ley4Documentos]);

  useEffect(() => {
    // Verificar que tenemos la API key configurada correctamente
    if (!process.env.REACT_APP_GEMINI_API_KEY) {
      console.error("Error: No se ha configurado la clave API de Gemini");
      setError("No se ha configurado correctamente el servicio de IA. Por favor, contacte con el administrador.");
    }
    
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [messages]);

  // Función para consultar todos los documentos en la colección ley4
  const obtenerDocumentosLey4 = async () => {
    try {
      const ley4Collection = collection(db, "ley4");
      const querySnapshot = await getDocs(ley4Collection);
      
      let documentos = [];
      querySnapshot.forEach((doc) => {
        // Guardar tanto el ID como los datos del documento
        documentos.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return documentos;
    } catch (error) {
      console.error("Error al obtener documentos de la Ley 4:", error);
      throw error;
    }
  };

  // Función para determinar si una consulta está relacionada con la Ley 4
  const esConsultaSobreLey4 = (texto) => {
    const patronesLey4 = [
      /ley 4/i,
      /ley 4 de 1976/i,
      /reajuste/i,
      /artículos? (de la )?ley 4/i,
      /pensiones/i,
      /derechos del pensionado/i,
      /incremento pensional/i,
      /aumento de pensión/i,
      /reajuste de pensión/i,
      /mesada/i
    ];
    
    return patronesLey4.some(patron => patron.test(texto));
  };

  // Crear función para detectar consultas relacionadas con pagos y pensiones
  const esConsultaSobrePagos = (texto) => {
    const patronesPagos = [
      /pago/i,
      /mesada/i,
      /pensión/i,
      /pensional/i,
      /incremento/i,
      /aumento/i,
      /valor/i,
      /monto/i,
      /cuánto recib/i,
      /histórico/i,
      /historial/i
    ];  // Array cerrado correctamente
    
    return patronesPagos.some(patron => patron.test(texto));
  };

  // Función para llamar directamente a la API de Gemini
  const llamarAPIGemini = async (prompt) => {
    try {
      const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${apiKey}`;
      
      const payload = {
        contents: [{
          parts: [{
            text: prompt
          }]
        }]
      };
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Error en la API de Gemini: ${errorData.error?.message || response.statusText}`);
      }
      
      const data = await response.json();
      
      // Verificar si hay una respuesta válida
      if (data.candidates && data.candidates.length > 0 && 
          data.candidates[0].content && 
          data.candidates[0].content.parts && 
          data.candidates[0].content.parts.length > 0) {
        return data.candidates[0].content.parts[0].text;
      } else {
        throw new Error("La API de Gemini no devolvió una respuesta válida");
      }
    } catch (error) {
      console.error("Error al llamar a la API de Gemini:", error);
      throw error;
    }
  };

  // Extrae documentos y nombres de la consulta
  const extraerConsulta = (texto) => {
    const documentosRegex = /\b\d{6,12}\b/g;
    const documentos = texto.match(documentosRegex) || [];
    
    let nombres = [];
    const patrones = [
      /(?:nombre|sobre|persona|beneficiario|causante|pensionado):?\s+([A-Za-zÁÉÍÓÚáéíóúÑñ\s]{3,30})/gi,
      /(?:de|para|a)\s+([A-Za-zÁÉÍÓÚáéíóúÑñ]{2,15}\s+[A-Za-zÁÉÍÓÚáéíóúÑñ]{2,15}(?:\s+[A-Za-zÁÉÍÓÚáéíóúÑñ]{2,15})?)/gi,
    ];
    
    patrones.forEach(patron => {
      let match;
      while ((match = patron.exec(texto)) !== null) {
        if (match[1] && match[1].trim()) {
          const nombre = match[1].trim().replace(/\s+/g, ' '); // Normaliza espacios
          nombres.push(nombre);
        }
      }
    });
    
    // Elimina duplicados
    nombres = [...new Set(nombres)];
    
    return { documentos, nombres };
  };

  // Función para obtener y almacenar datos del documento siguiendo el flujo correcto
  const obtenerYAlmacenarDatosUsuario = async (documento) => {
    try {
      console.log(`Iniciando proceso de consulta para documento: ${documento}`);
      
      // Lista de campos que necesitamos buscar (excluyendo DETALLE como se especificó)
      const camposNecesarios = [
        "DEMANDANTE",
        "CEDULA",
        "NACIMIENTO",
        "INGRESO_DISCONTINUO",
        "RETIRO_DISCONTINUO",
        "INGRESO",
        "EGRESO",
        "TIEMPO_LABORADO",
        "SUELDO_PROMEDIO",
        "JUBILACION_ELECTRICARIBE",
        "PENSION_JUBILACION_1999",
        "COMPARTICION",
        "PENSION_JUBILACION_2006",
        "PENSION_VEJEZ_2006",
        "MAYOR_VALOR_CARGO_EMPRESA",
        "EDAD_TERMINACION_CONTRATO",
        "CARGO",
        "DOMICILIO",
        "DIRECCION",
        "CELULAR", 
        "CORREO_ELECTRONICO",
        "ESTADO_CIVIL",
        "NOMBRE_CONYUGUE",
        "CELULAR_CONYUGUE",
        "NIT_FIDUPREVISORA",
        "NIT_COLPENSIONES",
        "FONDO_PATRIMONIO_AUTONOMO"
      ];

      // Normalizamos las claves para buscar en diferentes formatos
      const camposNormalizados = {
        "DEMANDANTE": ["DEMANDANTE", "demandante", "nombre"],
        "CEDULA": ["CEDULA", "cedula", "documento", "doc", "id"],
        "NACIMIENTO": ["NACIMIENTO", "nacimiento", "fecha_nacimiento", "FECHA DE NACIMIENTO", "fecha nacimiento"],
        "INGRESO_DISCONTINUO": ["INGRESO_DISCONTINUO", "INGRESO DISCONTINUO", "ingreso discontinuo"],
        "RETIRO_DISCONTINUO": ["RETIRO_DISCONTINUO", "RETIRO DISCONTINUO", "retiro discontinuo"],
        "INGRESO": ["INGRESO", "ingreso", "fecha_ingreso", "FECHA INGRESO"],
        "EGRESO": ["EGRESO", "egreso", "fecha_egreso", "FECHA EGRESO"],
        "TIEMPO_LABORADO": ["TIEMPO_LABORADO", "TIEMPO LABORADO", "tiempo laborado"],
        "SUELDO_PROMEDIO": ["SUELDO_PROMEDIO", "SUELDO PROMEDIO", "sueldo promedio"],
        "JUBILACION_ELECTRICARIBE": ["JUBILACION_ELECTRICARIBE", "JUBILACION ELECTRICARIBE", "jubilacion electricaribe"],
        "PENSION_JUBILACION_1999": ["PENSION_JUBILACION_1999", "PENSION DE JUBILACION 1999", "pension jubilacion 1999"],
        "COMPARTICION": ["COMPARTICION", "COMPARTICON", "comparticion", "comparticon"],
        "PENSION_JUBILACION_2006": ["PENSION_JUBILACION_2006", "PENSION DE JUBILACION AÑO 2006", "pension jubilacion 2006"],
        "PENSION_VEJEZ_2006": ["PENSION_VEJEZ_2006", "PENSION DE VEJEZ AÑO 2006", "pension vejez 2006"],
        "MAYOR_VALOR_CARGO_EMPRESA": ["MAYOR_VALOR_CARGO_EMPRESA", "MAYOR VALOR A CARGO EMPRESA", "mayor valor cargo empresa"],
        "EDAD_TERMINACION_CONTRATO": ["EDAD_TERMINACION_CONTRATO", "EDAD A LA FECHA DE TERMINACION CONTRATO", "edad terminacion"],
        "CARGO": ["CARGO", "cargo", "puesto"],
        "DOMICILIO": ["DOMICILIO", "domicilio"],
        "DIRECCION": ["DIRECCION", "direccion", "dirección"],
        "CELULAR": ["CELULAR", "celular", "telefono", "teléfono", "movil"],
        "CORREO_ELECTRONICO": ["CORREO_ELECTRONICO", "CORREO ELECTRONICO", "correo", "email"],
        "ESTADO_CIVIL": ["ESTADO_CIVIL", "ESTADO CIVIL", "estado civil"],
        "NOMBRE_CONYUGUE": ["NOMBRE_CONYUGUE", "NOMBRE DEL CONYUGUE", "conyugue", "cónyuge"],
        "CELULAR_CONYUGUE": ["CELULAR_CONYUGUE", "CELULAR CONYUGUE", "telefono conyugue"],
        "NIT_FIDUPREVISORA": ["NIT_FIDUPREVISORA", "NIT FIDUPREVISORA", "fiduprevisora"],
        "NIT_COLPENSIONES": ["NIT_COLPENSIONES", "NIT COLPENSIONES", "colpensiones"],
        "FONDO_PATRIMONIO_AUTONOMO": ["FONDO_PATRIMONIO_AUTONOMO", "El Fondo de Patrimonio Autónomo FONECA", "FONECA"]
      };

      // Objeto para almacenar todos los datos encontrados
      let datosCompletos = {
        iddoc: documento
      };

      // Lista de colecciones a consultar (en orden de prioridad)
      const colecciones = ["contextousuarios", "relaciondocumental", "datospensionados", "infodemandantes"];
      
      // Recorremos cada colección buscando los campos
      for (const coleccion of colecciones) {
        console.log(`Buscando en colección: ${coleccion}...`);
        try {
          const docRef = doc(db, coleccion, documento);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const datos = docSnap.data();
            console.log(`Datos encontrados en ${coleccion}`);
            
            // Verificamos cada campo necesario
            for (const campo of camposNecesarios) {
              // Si ya tenemos este campo, no lo sobrescribimos (respetamos la prioridad de las colecciones)
              if (datosCompletos[campo] !== undefined) continue;
              
              // Buscamos el campo en todas sus posibles variantes
              for (const variante of camposNormalizados[campo]) {
                if (datos[variante] !== undefined) {
                  datosCompletos[campo] = datos[variante];
                  console.log(`Campo ${campo} encontrado como ${variante}`);
                  break;
                }
              }
            }
            
            // También guardamos la colección completa para referencia
            datosCompletos[`datos_${coleccion}`] = datos;
          }
        } catch (error) {
          console.warn(`Error al consultar colección ${coleccion}:`, error);
          // Continuamos con la siguiente colección
        }
      }
      
      // Si después de buscar en todas las colecciones no tenemos datos suficientes,
      // intentamos con la API de consolidación
      const camposEncontrados = Object.keys(datosCompletos).filter(k => camposNecesarios.includes(k));
      console.log(`Se encontraron ${camposEncontrados.length} de ${camposNecesarios.length} campos necesarios`);
      
      if (camposEncontrados.length < camposNecesarios.length * 0.3) {  // Si tenemos menos del 30% de los campos
        console.log("Datos insuficientes, intentando consolidación mediante API...");
        
        try {
          const consolidarUrl = `https://consolidardocumento-w4tv3jcmvq-uc.a.run.app?iddoc=${documento}`;
          console.log(`Ejecutando API de consolidación: ${consolidarUrl}`);
          
          const response = await fetch(consolidarUrl);
          if (response.ok) {
            console.log("API de consolidación ejecutada");
            
            // Esperamos un poco para que se completen los procesos de consolidación
            await new Promise(resolve => setTimeout(resolve, 3000));
            
            // Volvemos a intentar las consultas a las colecciones
            for (const coleccion of colecciones) {
              try {
                const docRef = doc(db, coleccion, documento);
                const docSnap = await getDoc(docRef);
                
                if (docSnap.exists()) {
                  const datos = docSnap.data();
                  
                  // Verificamos cada campo necesario de nuevo
                  for (const campo of camposNecesarios) {
                    if (datosCompletos[campo] !== undefined) continue;
                    
                    for (const variante of camposNormalizados[campo]) {
                      if (datos[variante] !== undefined) {
                        datosCompletos[campo] = datos[variante];
                        break;
                      }
                    }
                  }
                  
                  // Actualizamos los datos completos de la colección
                  datosCompletos[`datos_${coleccion}`] = datos;
                }
              } catch (error) {
                console.warn(`Error al consultar colección ${coleccion} después de consolidación:`, error);
              }
            }
          }
        } catch (apiError) {
          console.error("Error al llamar a la API de consolidación:", apiError);
        }
      }
      
      // Estructuramos los datos para devolver
      const datosEstructurados = {
        iddoc: documento,
        // Creamos perfil con todos los campos normalizados
        perfil: {
          DEMANDANTE: datosCompletos.DEMANDANTE || null,
          CEDULA: datosCompletos.CEDULA || documento,
          NACIMIENTO: datosCompletos.NACIMIENTO || null,
          INGRESO_DISCONTINUO: datosCompletos.INGRESO_DISCONTINUO || null,
          RETIRO_DISCONTINUO: datosCompletos.RETIRO_DISCONTINUO || null,
          INGRESO: datosCompletos.INGRESO || null,
          EGRESO: datosCompletos.EGRESO || null,
          TIEMPO_LABORADO: datosCompletos.TIEMPO_LABORADO || null,
          SUELDO_PROMEDIO: datosCompletos.SUELDO_PROMEDIO || null,
          JUBILACION_ELECTRICARIBE: datosCompletos.JUBILACION_ELECTRICARIBE || null,
          PENSION_JUBILACION_1999: datosCompletos.PENSION_JUBILACION_1999 || null,
          COMPARTICION: datosCompletos.COMPARTICION || null,
          PENSION_JUBILACION_2006: datosCompletos.PENSION_JUBILACION_2006 || null,
          PENSION_VEJEZ_2006: datosCompletos.PENSION_VEJEZ_2006 || null,
          MAYOR_VALOR_CARGO_EMPRESA: datosCompletos.MAYOR_VALOR_CARGO_EMPRESA || null,
          EDAD_TERMINACION_CONTRATO: datosCompletos.EDAD_TERMINACION_CONTRATO || null,
          CARGO: datosCompletos.CARGO || null,
          DOMICILIO: datosCompletos.DOMICILIO || null,
          DIRECCION: datosCompletos.DIRECCION || null,
          CELULAR: datosCompletos.CELULAR || null,
          CORREO_ELECTRONICO: datosCompletos.CORREO_ELECTRONICO || null,
          ESTADO_CIVIL: datosCompletos.ESTADO_CIVIL || null,
          NOMBRE_CONYUGUE: datosCompletos.NOMBRE_CONYUGUE || null,
          CELULAR_CONYUGUE: datosCompletos.CELULAR_CONYUGUE || null,
          NIT_FIDUPREVISORA: datosCompletos.NIT_FIDUPREVISORA || null,
          NIT_COLPENSIONES: datosCompletos.NIT_COLPENSIONES || null,
          FONDO_PATRIMONIO: datosCompletos.FONDO_PATRIMONIO_AUTONOMO || null
        },
        // Guardamos los datos originales de las distintas colecciones
        datosOriginales: datosCompletos,
        timestamp: new Date().toISOString()
      };
      
      // Guardamos el resultado consolidado en contextousuarios para acelerar consultas futuras
      try {
        const contextoRef = doc(db, "contextousuarios", documento);
        await setDoc(contextoRef, datosEstructurados);
        console.log("Datos consolidados guardados en contextousuarios para consultas futuras");
      } catch (saveError) {
        console.error("Error al guardar datos consolidados:", saveError);
        // No interrumpimos el flujo si este guardado falla
      }
      
      // Determinamos si encontramos suficiente información
      const camposPerfilNoNulos = Object.values(datosEstructurados.perfil).filter(v => v !== null).length;
      const porcentajeCompleto = (camposPerfilNoNulos / camposNecesarios.length) * 100;
      
      return {
        success: true,
        datos: datosEstructurados,
        fuente: "multiple",
        porcentajeCompleto: porcentajeCompleto.toFixed(1),
        mensaje: `Datos recuperados: ${camposPerfilNoNulos} campos de ${camposNecesarios.length} (${porcentajeCompleto.toFixed(1)}%)`
      };
      
    } catch (error) {
      console.error("Error al obtener datos del usuario:", error);
      return {
        success: false,
        error: error.message,
        mensaje: "Error al obtener información del documento"
      };
    }
  };

  // Función para procesar la consulta with Gemini usando llamada directa a la API
  const procesarConsultaGemini = async (pregunta, contextoDatos = null) => {
    try {
      console.log("Procesando consulta con Gemini...");
      
      // Preparar el prompt base
      let promptBase = `Eres un asistente legal especializado en derecho pensional colombiano. 
      Responde a la siguiente consulta del usuario: "${pregunta}"`;
      
      // CAMBIO PRINCIPAL: Priorizar el contexto del usuario si está disponible
      if (contextoDatos) {
        console.log("Añadiendo información de contexto del usuario a la consulta...");
        
        promptBase += `\n\nIMPORTANTE: Basa tu respuesta principalmente en la siguiente información del usuario consultado:
        \n${JSON.stringify(contextoDatos, null, 2)}
        \nSi la información solicitada está en estos datos, prioriza siempre esta fuente.`;
      }
      
      // NUEVO: Añadir información de análisis de pagos si está disponible
      if (datosPagos && Object.keys(datosPagos).length > 0) {
        console.log("Añadiendo información de análisis de pagos a la consulta...");
        
        promptBase += `\n\nINFORMACIÓN DE PAGOS: Utiliza estos datos sobre pagos de pensión para responder consultas relacionadas:
        \n${JSON.stringify(datosPagos, null, 2)}
        \nEsta información contiene estadísticas, resumen y detalles de los pagos pensionales del usuario.`;
      }
      
      // NUEVO: Añadir información de documentos relacionados si está disponible
      if (datosDocumentosRelacionados && datosDocumentosRelacionados.documentos) {
        console.log("Añadiendo información de documentos relacionados a la consulta...");
        
        promptBase += `\n\nINFORMACIÓN ADICIONAL: Considera los siguientes documentos relacionados con este caso:`;
        
        // Añadir hasta 5 documentos más relevantes (para no exceder límites de tokens)
        const documentosRelevantes = datosDocumentosRelacionados.documentos
          .filter(doc => doc.texto && doc.texto.length > 0)
          .slice(0, 5);
        
        documentosRelevantes.forEach((doc, index) => {
          promptBase += `\n\nDOCUMENTO ${index + 1} (ID: ${doc.docId || 'sin-id'}):\n${doc.texto.substring(0, 2000)}`;
          if (doc.texto.length > 2000) promptBase += "...[contenido truncado]";
        });
        
        promptBase += `\n\nEsta información es específica del caso y puede ser relevante para responder a la consulta.`;
      }
      
      // Verificar si la consulta es sobre la Ley 4 como información complementaria
      const esConsultaLey4 = esConsultaSobreLey4(pregunta);
      
      // Agregar información de la Ley 4 solo como referencia complementaria
      if (esConsultaLey4 && ley4Documentos && ley4Documentos.length > 0) {
        console.log("Añadiendo documentos de la Ley 4 como información complementaria...");
        
        promptBase += `\n\nComo información complementaria, puedes consultar estos documentos sobre la Ley 4 de 1976, 
        pero solo si encuentras la respuesta en los datos específicos del usuario proporcionados anteriormente:
        \n\n`;
        
        // Añadir contenido de los documentos
        ley4Documentos.forEach(doc => {
          if (doc.titulo && doc.contenido) {
            promptBase += `--- ${doc.titulo} ---\n${doc.contenido}\n\n`;
          } else {
            promptBase += `--- Documento ${doc.id} ---\n${JSON.stringify(doc)}\n\n`;
          }
        });
        
        promptBase += `\nRecuerda: La información específica del usuario tiene prioridad sobre estos documentos generales.`;
      }
      
      // Instrucciones finales para asegurar respuestas centradas en el usuario
      promptBase += `\n\nInstrucciones finales:
      1. Si la consulta es sobre datos específicos del usuario y estos datos están disponibles, responde con esa información precisa.
      2. Si la consulta es sobre la Ley 4 pero tiene relación con el usuario, contextualiza la respuesta con los datos del usuario.
      3. Si no tienes información específica sobre lo que se pregunta en los datos proporcionados, indícalo claramente.
      4. Sé conciso y directo en tu respuesta.`;
      
      // Llamar directamente a la API de Gemini
      const respuestaTexto = await llamarAPIGemini(promptBase);
      
      console.log("Respuesta de Gemini recibida correctamente");
      
      return {
        success: true,
        respuesta: respuestaTexto,
        contexto: contextoDatos ? { datos: { encontrados: true } } : null,
        usoDatosLey4: esConsultaLey4 && ley4Documentos && ley4Documentos.length > 0,
        usoDatosUsuario: !!contextoDatos
      };
    } catch (error) {
      console.error("Error al procesar consulta con Gemini:", error);
      return {
        success: false,
        error: error.message
      };
    }
  };

  const verificarCalidadRespuesta = (respuesta, contexto) => {
    if (contexto?.datos?.encontrados && 
        (respuesta.includes("Ley 4 de 1976 establece") || 
         respuesta.includes("consultar con un abogado especializado"))) {
      return {
        esGenerica: true,
        mensajeAdicional: "*Nota: He encontrado datos específicos sobre esta consulta pero necesito más información para darte detalles precisos.*"
      };
    }
    return { esGenerica: false };
  };

  // Función para procesar consultas relacionadas con documentos
  const procesarConsultaDocumentos = (pregunta) => {
    const patrones = [
      /ver documentos relacionados/i,
      /mostrar documentos/i,
      /documentos asociados/i,
      /acuerdo marco/i,
      /sintraelecol/i,
      /ver acuerdos/i
    ];
    
    return patrones.some(patron => patron.test(pregunta));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    setLoading(true);
    setError(null);

    const currentInput = input;
    setInput('');
    setMessages((prev) => [...prev, { sender: 'user', message: currentInput }]);

    // Comentamos este bloque para que no abra el modal y en su lugar use los datos para responder
    /* 
    // Verificar si la consulta es sobre análisis de pagos
    const esPreguntaAnalisisPagos = currentInput.toLowerCase().includes('análisis') && 
      (currentInput.toLowerCase().includes('pago') || 
       currentInput.toLowerCase().includes('mesada') || 
       currentInput.toLowerCase().includes('pensión') ||
       currentInput.toLowerCase().includes('incremento'));

    if (esPreguntaAnalisisPagos && contextoActivo) {
      setMostrarAnalisisPagos(true);
      setMessages((prev) => [...prev, { 
        sender: 'system', 
        message: 'He activado el análisis detallado de pagos donde puedes ver las estadísticas de las mesadas pensionales. Puedes revisar la información arriba.' 
      }]);
      setLoading(false);
      return;
    }
    */

    try {
      const { documentos, nombres } = extraerConsulta(currentInput);

      // Si hay documentos identificados y no hay un contexto activo, obtenemos los datos
      if (documentos.length > 0 && !contextoActivo) {
        setMessages((prev) => [...prev, { 
          sender: 'system', 
          message: `Estoy buscando información sobre el documento ${documentos[0]}...` 
        }]);
        
        console.log(`Procesando documento: ${documentos[0]}`);
        const resultado = await obtenerYAlmacenarDatosUsuario(documentos[0]);
        
        if (resultado.success) {
          // Actualizar el contexto con los datos obtenidos
          const nuevoContexto = {
            iddoc: documentos[0],
            datos: {
              encontrados: true,
              ...resultado.datos
            },
            descripcion: `Documento ${documentos[0]}`,
            timestamp: new Date().toISOString()
          };
          
          setContextoActivo(nuevoContexto);
          
          setMessages((prev) => [...prev, { 
            sender: 'system', 
            message: `He encontrado información sobre el documento ${documentos[0]}. ¿Qué deseas saber específicamente?` 
          }]);
          setLoading(false);
          return;
        } else {
          setMessages((prev) => [...prev, { 
            sender: 'system', 
            message: `No encontré información específica para el documento ${documentos[0]}. Intentaré responder con información general.` 
          }]);
        }
      }

      // Procesar la consulta con Gemini
      console.log("Procesando consulta:", currentInput);
      let response;
      
      if (contextoActivo) {
        console.log("Consulta con contexto activo");
        response = await procesarConsultaGemini(currentInput, contextoActivo.datos);
      } else {
        console.log("Consulta general sin contexto");
        response = await procesarConsultaGemini(currentInput);
      }

      if (response.success) {
        const { esGenerica, mensajeAdicional } = verificarCalidadRespuesta(
          response.respuesta, 
          response.contexto
        );
        
        let mensaje = response.respuesta;
        
        // Indicador adicional para respuestas basadas en diferentes fuentes
        const esPreguntaPagos = esConsultaSobrePagos(currentInput);
        const tieneDatosPagos = datosPagos && datosPagos.pagosDisponibles;
        const esPreguntaDoc = procesarConsultaDocumentos(currentInput);
        const tieneDatosDoc = datosDocumentosRelacionados && datosDocumentosRelacionados.documentos?.length > 0;
        
        // Indicar cuando se usa información específica del usuario
        if (response.usoDatosUsuario) {
          mensaje += "\n\n*Respuesta basada en la información específica del usuario consultado.*";
        } 
        // Indicar cuando se usan datos de pagos
        if (esPreguntaPagos && tieneDatosPagos) {
          mensaje += "\n\n*Esta respuesta incluye análisis de pagos de pensión del usuario consultado.*";
          response.usoDatosPagos = true;
        }
        // Indicar cuando se usan documentos relacionados
        if (esPreguntaDoc && tieneDatosDoc) {
          mensaje += "\n\n*Esta respuesta incluye información de documentos relacionados con el caso.*";
          response.usoDatosDocumentos = true;
        }
        // Solo indicar el uso de documentos de la Ley 4 si NO se usaron otras fuentes
        else if (response.usoDatosLey4 && !(response.usoDatosUsuario || response.usoDatosPagos || response.usoDatosDocumentos)) {
          mensaje += "\n\n*Respuesta basada en la documentación de la Ley 4 de 1976.*";
        }
        
        if (esGenerica && mensajeAdicional) {
          mensaje = `${mensaje}\n\n${mensajeAdicional}`;
        }
        
        setMessages((prev) => [...prev, {
          sender: 'system',
          message: mensaje
        }]);
      } else {
        throw new Error(response.error || 'No se pudo procesar la consulta');
      }
    } catch (err) {
      console.error('Error al enviar consulta legal:', err);
      let errorMessage = 'Hubo un problema al procesar tu consulta.';
      
      if (process.env.NODE_ENV === 'development') {
        errorMessage += ' Detalles: ' + err.message || 'Error desconocido';
      }
      
      setError(errorMessage);
      setMessages((prev) => [...prev, {
        sender: 'error',
        message: 'Lo siento, ocurrió un error al procesar tu consulta. Por favor, intenta de nuevo.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleReiniciarConsulta = () => {
    setContextoActivo(null);
    setMessages([
      { sender: 'system', message: 'Bienvenido al asistente legal especializado en derecho pensional. ¿En qué puedo ayudarte hoy? Puedes preguntar sobre una persona específica por su nombre o número de documento.' }
    ]);
  };

  const handleDatosDocumentos = (datos) => {
    console.log("Recibiendo datos de DocumentosRelacionados:", datos);
    setDatosDocumentosRelacionados(datos);
  };

  // Modificar el efecto donde se establece el contexto activo
  // Buscar el bloque donde se actualiza setContextoActivo y añadir:
  useEffect(() => {
    if (contextoActivo) {
      // Verificar si ya tenemos datos de documentos para este usuario
      if (!datosDocumentosRelacionados && contextoActivo.iddoc) {
        console.log("Cargando datos de documentos relacionados en segundo plano...");
        
        // Cargar documentos en segundo plano para tenerlos listos
        const cargarDocumentosEnSegundoPlano = async () => {
          try {
            const docRef = doc(db, "relaciondocumental", contextoActivo.iddoc);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
              const datos = procesarDatosDocumentos(docSnap.data());
              handleDatosDocumentos(datos);
            } else {
              console.log("No se encontraron documentos relacionados para este ID");
            }
          } catch (error) {
            console.error("Error al cargar documentos relacionados en segundo plano:", error);
          }
        };
        
        cargarDocumentosEnSegundoPlano();
      }
    }
  }, [contextoActivo]);

  // Modificar el efecto para cargar también los datos de pagos
  useEffect(() => {
    if (contextoActivo) {
      // Verificar si ya tenemos datos de documentos para este usuario
      if (!datosDocumentosRelacionados && contextoActivo.iddoc) {
        console.log("Cargando datos de documentos relacionados en segundo plano...");
        
        // Cargar documentos en segundo plano para tenerlos listos
        const cargarDocumentosEnSegundoPlano = async () => {
          try {
            const docRef = doc(db, "relaciondocumental", contextoActivo.iddoc);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
              const datos = procesarDatosDocumentos(docSnap.data());
              handleDatosDocumentos(datos);
            } else {
              console.log("No se encontraron documentos relacionados para este ID");
            }
          } catch (error) {
            console.error("Error al cargar documentos relacionados en segundo plano:", error);
          }
        };
        
        cargarDocumentosEnSegundoPlano();
      }
      
      // Cargar datos de pagos en segundo plano
      if (!datosPagos && contextoActivo.iddoc) {
        console.log("Cargando datos de análisis de pagos en segundo plano...");
        
        const cargarDatosPagosEnSegundoPlano = async () => {
          try {
            const datos = await obtenerDatosPagos(contextoActivo.iddoc);
            if (datos && datos.pagosDisponibles) {
              setDatosPagos(datos);
              console.log("Datos de pagos cargados exitosamente", datos); // <-- Agrega este console.log
            }
          } catch (error) {
            console.error("Error al cargar datos de pagos en segundo plano:", error);
          }
        };
        
        cargarDatosPagosEnSegundoPlano();
      }
    }
  }, [contextoActivo, datosDocumentosRelacionados, datosPagos]);

  // Añadir esta función auxiliar:
  const procesarDatosDocumentos = (data) => {
    if (!data) return null;
    
    let documentosExtraidos = [];
    
    if (data.documentos && Array.isArray(data.documentos)) {
      const docsValidos = data.documentos
        .filter(item => item && typeof item === 'object' && item.texto)
        .map(item => ({
          docId: item.id || item.nombre || 'sin-id',
          texto: item.texto,
          nombre: item.nombre || '',
          fecha: item.fecha || ''
        }));
      
      documentosExtraidos = [...documentosExtraidos, ...docsValidos];
    }
    
    if (documentosExtraidos.length === 0 && data.contenidoTextual && Array.isArray(data.contenidoTextual)) {
      const docsTextualValidos = data.contenidoTextual
        .filter(item => item && typeof item === 'object' && item.texto)
        .map(item => ({
          docId: item.docId || 'sin-id',
          texto: item.texto
        }));
      
      documentosExtraidos = [...documentosExtraidos, ...docsTextualValidos];
    }
    
    return {
      documentos: documentosExtraidos,
      datosOriginales: data
    };
  };

  // Añadir esta función para obtener datos de pagos
  const obtenerDatosPagos = async (documento) => {
    try {
      console.log("Obteniendo datos de pagos para:", documento);
      
      // Consultamos la colección de pagos para este documento
      const pagosRef = collection(db, "pagos");
      const q = query(pagosRef, where("cedula", "==", documento));
      const querySnapshot = await getDocs(q);
      
      let datosPagos = {
        pagosDisponibles: false,
        resumen: {},
        pagos: []
      };
      
      if (!querySnapshot.empty) {
        // Recolectar todos los pagos
        querySnapshot.forEach((doc) => {
          const pago = doc.data();
          datosPagos.pagos.push(pago);
        });
        
        // Si hay pagos, calculamos estadísticas
        if (datosPagos.pagos.length > 0) {
          datosPagos.pagosDisponibles = true;
          
          // Calcular estadísticas: promedio, total, max, min, etc.
          const valoresPago = datosPagos.pagos
            .filter(p => p.valor && !isNaN(parseFloat(p.valor)))
            .map(p => parseFloat(p.valor));
          
          if (valoresPago.length > 0) {
            datosPagos.resumen = {
              totalPagos: datosPagos.pagos.length,
              valorPromedio: valoresPago.reduce((a, b) => a + b, 0) / valoresPago.length,
              valorMaximo: Math.max(...valoresPago),
              valorMinimo: Math.min(...valoresPago),
              fechaInicio: new Date(Math.min(...datosPagos.pagos.map(p => new Date(p.fecha || 0)))).toISOString().split('T')[0],
              fechaFin: new Date(Math.max(...datosPagos.pagos.map(p => new Date(p.fecha || 0)))).toISOString().split('T')[0],
            };
          }
        }
      }
      
      return datosPagos;
    } catch (error) {
      console.error("Error al obtener datos de pagos:", error);
      return { 
        pagosDisponibles: false, 
        error: error.message 
      };
    }
  };

  // Añadir esta función antes del return
  const handleAnalisisReajuste = async () => {
    // Solo proceder si hay contexto activo
    if (!contextoActivo) {
      setMessages((prev) => [...prev, { 
        sender: 'system', 
        message: 'Para realizar un análisis de reajuste, primero debes consultar un pensionado específico por su número de documento.' 
      }]);
      return;
    }

    setLoading(true);
    setMessages((prev) => [...prev, { 
      sender: 'user', 
      message: `Solicito un análisis completo de reajuste pensional para el documento ${contextoActivo.iddoc} según la Ley 4 de 1976 y normativa vigente.` 
    }]);

    try {
      // Aseguramos que tenemos los datos necesarios
      let tieneDatosPagos = datosPagos && datosPagos.pagosDisponibles;
      let tieneDatosDoc = datosDocumentosRelacionados && datosDocumentosRelacionados.documentos?.length > 0;
      
      // Si no tenemos datos de pagos, intentamos cargarlos
      if (!tieneDatosPagos) {
        console.log("Cargando datos de pagos para análisis de reajuste...");
        const datos = await obtenerDatosPagos(contextoActivo.iddoc);
        if (datos && datos.pagosDisponibles) {
          setDatosPagos(datos);
          tieneDatosPagos = true;
        }
      }
      
      // Si no tenemos documentos relacionados, intentamos cargarlos
      if (!tieneDatosDoc) {
        console.log("Cargando documentos relacionados para análisis de reajuste...");
        try {
          const docRef = doc(db, "relaciondocumental", contextoActivo.iddoc);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const datos = procesarDatosDocumentos(docSnap.data());
            handleDatosDocumentos(datos);
            tieneDatosDoc = datos.documentos && datos.documentos.length > 0;
          }
        } catch (error) {
          console.error("Error al cargar documentos relacionados:", error);
        }
      }
      
      // Generamos un prompt especializado para análisis de reajuste
      let promptReajuste = `Eres un experto en derecho pensional colombiano especializado en reajustes según la Ley 4 de 1976 y normativa relacionada.
      
      TAREA: Analiza si el pensionado con documento ${contextoActivo.iddoc} tiene derecho a un reajuste pensional, considerando todas las fuentes de información disponibles.
      
      CRITERIOS DE ANÁLISIS:
      1. Verificación si la pensión fue otorgada antes de 1989 (ámbito de aplicación de la Ley 4)
      2. Comparación de montos actuales con los mínimos legales vigentes
      3. Si ha recibido reajustes anteriores y si fueron correctamente aplicados
      4. Situaciones de especial vulnerabilidad o condiciones específicas mencionadas en documentos
      5. Validez temporal de posibles reclamaciones (prescripción)
      
      INFORMACIÓN ESPECÍFICA DEL PENSIONADO:`;
      
      // Añadir datos del usuario
      if (contextoActivo && contextoActivo.datos) {
        promptReajuste += `\n\nDATOS PERSONALES Y LABORALES:\n${JSON.stringify(contextoActivo.datos, null, 2)}`;
      }
      
      //Añadir tabla de salarios mínimos y auxilio de transporte
      promptReajuste += `\n\nTABLA DE SALARIOS MÍNIMOS Y AUXILIO DE TRANSPORTE (COP):\n`;
      promptReajuste += `Año\tSalario Mínimo Mensual (COP)\tAuxilio de Transporte (COP)\n`;
      promptReajuste += `1976\t$1.560\tNo aplica\n`;
      promptReajuste += `1977\t$1.860\tNo aplica\n`;
      promptReajuste += `1978\t$2.580\tNo aplica\n`;
      promptReajuste += `1979\t$3.450\tNo aplica\n`;
      promptReajuste += `1980\t$4.500\tNo aplica\n`;
      promptReajuste += `1981\t$5.700\tNo aplica\n`;
      promptReajuste += `1982\t$7.410\tNo aplica\n`;
      promptReajuste += `1983\t$9.261\tNo aplica\n`;
      promptReajuste += `1984\t$11.298\tNo aplica\n`;
      promptReajuste += `1985\t$13.558\tNo aplica\n`;
      promptReajuste += `1986\t$16.811\tNo aplica\n`;
      promptReajuste += `1987\t$20.510\tNo aplica\n`;
      promptReajuste += `1988\t$25.637\tNo aplica\n`;
      promptReajuste += `1989\t$32.560\t$3.063\n`;
      promptReajuste += `1990\t$41.025\t$3.798\n`;
      promptReajuste += `1991\t$51.720\t$4.787\n`;
      promptReajuste += `1992\t$65.190\t$6.033\n`;
      promptReajuste += `1993\t$81.510\t$7.542\n`;
      promptReajuste += `1994\t$98.700\t$8.705\n`;
      promptReajuste += `1995\t$118.934\t$10.815\n`;
      promptReajuste += `1996\t$142.125\t$13.567\n`;
      promptReajuste += `1997\t$172.005\t$17.250\n`;
      promptReajuste += `1998\t$203.826\t$20.700\n`;
      promptReajuste += `1999\t$236.460\t$24.012\n`;
      promptReajuste += `2000\t$260.100\t$26.413\n`;
      promptReajuste += `2001\t$286.000\t$30.000\n`;
      promptReajuste += `2002\t$309.000\t$34.000\n`;
      promptReajuste += `2003\t$332.000\t$37.500\n`;
      promptReajuste += `2004\t$358.000\t$41.600\n`;
      promptReajuste += `2005\t$381.500\t$44.500\n`;
      promptReajuste += `2006\t$408.000\t$47.700\n`;
      promptReajuste += `2007\t$433.700\t$50.800\n`;
      promptReajuste += `2008\t$461.500\t$55.000\n`;
      promptReajuste += `2009\t$496.900\t$59.300\n`;
      promptReajuste += `2010\t$515.000\t$61.500\n`;
      promptReajuste += `2011\t$535.600\t$63.600\n`;
      promptReajuste += `2012\t$566.700\t$67.800\n`;
      promptReajuste += `2013\t$589.500\t$70.500\n`;
      promptReajuste += `2014\t$616.000\t$72.000\n`;
      promptReajuste += `2015\t$644.350\t$74.000\n`;
      promptReajuste += `2016\t$689.455\t$77.700\n`;
      promptReajuste += `2017\t$737.717\t$83.140\n`;
      promptReajuste += `2018\t$781.242\t$88.211\n`;
      promptReajuste += `2019\t$828.116\t$97.032\n`;
      promptReajuste += `2020\t$877.803\t$102.854\n`;
      promptReajuste += `2021\t$908.526\t$106.454\n`;
      promptReajuste += `2022\t$1.000.000\t$117.172\n`;
      promptReajuste += `2023\t$1.160.000\t$140.606\n`;
      promptReajuste += `2024\t$1.300.000\t$162.000\n`;
      
      // Añadir documentos de la ley 4
      if (ley4Documentos && ley4Documentos.length > 0) {
        promptReajuste += `\n\nBASE LEGAL (Ley 4 de 1976 y normativa relacionada):`;
        ley4Documentos.forEach(doc => {
          if (doc.titulo && doc.contenido) {
            promptReajuste += `\n\n--- ${doc.titulo} ---\n${doc.contenido}`;
          } else {
            promptReajuste += `\n\n--- Documento ${doc.id} ---\n${JSON.stringify(doc)}`;
          }
        });
      }
      
      // Añadir datos de pagos si están disponibles
      if (tieneDatosPagos) {
        promptReajuste += `\n\nHISTORIAL DE PAGOS PENSIONALES:\n`;
        
        // Añadir resumen estadístico
        if (datosPagos.resumen) {
          promptReajuste += `Resumen estadístico:\n`;
          promptReajuste += `- Total de pagos registrados: ${datosPagos.resumen.totalPagos}\n`;
          promptReajuste += `- Valor promedio: ${datosPagos.resumen.valorPromedio?.toLocaleString('es-CO', { style: 'currency', currency: 'COP' }) || 'No disponible'}\n`;
          promptReajuste += `- Valor mínimo: ${datosPagos.resumen.valorMinimo?.toLocaleString('es-CO', { style: 'currency', currency: 'COP' }) || 'No disponible'}\n`;
          promptReajuste += `- Valor máximo: ${datosPagos.resumen.valorMaximo?.toLocaleString('es-CO', { style: 'currency', currency: 'COP' }) || 'No disponible'}\n`;
          promptReajuste += `- Período registrado: ${datosPagos.resumen.fechaInicio || 'N/A'} hasta ${datosPagos.resumen.fechaFin || 'N/A'}\n`;
        }
        
        // Añadir últimos 5 pagos como referencia
        if (datosPagos.pagos && datosPagos.pagos.length > 0) {
          promptReajuste += `\nDetalle de últimos pagos (muestra):\n`;
          datosPagos.pagos
            .sort((a, b) => new Date(b.fecha || 0) - new Date(a.fecha || 0))
            .slice(0, 5)
            .forEach((pago, idx) => {
              promptReajuste += `${idx + 1}. Fecha: ${pago.fecha || 'No especificada'}, Valor: ${pago.valor || 'No especificado'}, Concepto: ${pago.concepto || 'No especificado'}\n`;
            });
        }
      }
      
      // Añadir información de documentos relacionados
      if (tieneDatosDoc) {
        promptReajuste += `\n\nDOCUMENTOS RELACIONADOS CON EL CASO:`;
        
        datosDocumentosRelacionados.documentos
          .filter(doc => doc.texto && doc.texto.length > 0)
          .slice(0, 3) // Limitamos a 3 para no exceder tokens
          .forEach((doc, index) => {
            promptReajuste += `\n\nDOCUMENTO ${index + 1} (ID: ${doc.docId || 'sin-id'}):\n${doc.texto.substring(0, 1500)}`;
            if (doc.texto.length > 1500) promptReajuste += "...[contenido truncado]";
          });
      }
      
      promptReajuste += `\n\nINSTRUCCIONES FINALES:
      1. Proporciona un análisis jurídico estructurado sobre el derecho al reajuste pensional.
      2. Incluye una conclusión clara indicando si tiene derecho o no al reajuste, o si falta información crucial.
      3. Si corresponde un reajuste, estima aproximadamente el porcentaje o monto que correspondería.
      4. Incluye recomendaciones específicas sobre los pasos a seguir.
      5. Tu respuesta debe ser precisa, bien fundamentada en la ley, y considerar todos los datos disponibles del pensionado.`;
      
      // Llamar directamente a la API de Gemini
      const respuestaTexto = await llamarAPIGemini(promptReajuste);
      
      // Mostrar la respuesta en el chat
      setMessages((prev) => [...prev, {
        sender: 'system',
        message: respuestaTexto + "\n\n*Este análisis se basa en la documentación de la Ley 4 de 1976, datos personales del pensionado, historial de pagos y documentos relacionados disponibles en el sistema.*"
      }]);
      
    } catch (error) {
      console.error("Error al realizar análisis de reajuste:", error);
      setMessages((prev) => [...prev, {
        sender: 'error',
        message: 'Lo siento, ocurrió un error al realizar el análisis de reajuste. Por favor, intenta nuevamente.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="legal-chat-container">
      <div className="legal-chat-header">
        <h1>Asistente Legal Pensional</h1>
        <p>Especializado en derecho de los pensionados</p>
        {contextoActivo && (
          <div className="documento-consulta-info">
            <p>
              <span className="contexto-badge">Consulta activa</span> 
              {contextoActivo.descripcion}
            </p>
            <div className="acciones-contexto">
              <button onClick={handleReiniciarConsulta} className="reiniciar-consulta-btn">
                Nueva consulta
              </button>
              <button 
                onClick={() => setMostrarAnalisisPagos(!mostrarAnalisisPagos)} 
                className="analisis-pagos-btn"
              >
                {mostrarAnalisisPagos ? 'Ocultar análisis de pagos' : 'Ver análisis de pagos'}
              </button>
              <button 
                onClick={() => setMostrarDocumentosRelacionados(!mostrarDocumentosRelacionados)} 
                className="documentos-relacionados-btn"
              >
                {mostrarDocumentosRelacionados ? 'Ocultar documentos' : 'Ver documentos relacionados'}
              </button>
            </div>
          </div>
        )}
      </div>
      <AnalisisPagos 
        contextoUsuario={contextoActivo} 
        isOpen={contextoActivo && mostrarAnalisisPagos}
        onClose={() => setMostrarAnalisisPagos(false)}
      />
      <DocumentosRelacionados
        iddoc={contextoActivo?.iddoc}
        isOpen={contextoActivo && mostrarDocumentosRelacionados}
        onClose={() => setMostrarDocumentosRelacionados(false)}
        onDatosObtenidos={handleDatosDocumentos}
      />
      <div className="legal-chat-window" ref={chatWindowRef}>
        {messages.map((msg, index) => (
          <div key={index} className={`legal-chat-message ${msg.sender}-message`}>
            <div className="message-content">
              <div className="message-sender">
                {msg.sender === 'user' ? 'Tú' : msg.sender === 'system' ? 'Asistente Legal' : 'Error'}
              </div>
              <div className="message-text">
                <ReactMarkdown>{msg.message}</ReactMarkdown>
              </div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="legal-chat-message system-message">
            <div className="message-content">
              <div className="message-sender">Asistente Legal</div>
              <div className="message-text loading-indicator">
                <div className="dot-flashing"></div>
              </div>
            </div>
          </div>
        )}
      </div>
      <form onSubmit={handleSendMessage} className="legal-chat-input-container">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Escribe tu consulta legal aquí..."
          className="legal-chat-input"
          rows={3}
          disabled={loading}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage(e);
            }
          }}
        />
        <div className="legal-chat-buttons">
          <button
            type="button"
            className="legal-chat-reajuste-button"
            onClick={handleAnalisisReajuste}
            disabled={loading || !contextoActivo}
          >
            Análisis Reajuste
          </button>
          <button
            type="submit"
            className="legal-chat-send-button"
            disabled={loading || !input.trim()}
          >
            {loading ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </form>
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
    </div>
  );
};

export default ChatLegal;
